import React from "react";
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";

import { PublicLayout } from "../layouts/PublicLayout";
import { MacDashboardLayout } from "../layouts/MacDashboardLayout";
import { RequireAuth, RequireAnyPermission } from "../../core/auth/routeGuard";

import { Home } from "../pages/public/Home";
import { About } from "../pages/public/About";
import { Login } from "../pages/auth/Login";

import { Forbidden } from "../pages/system/Forbidden";
import { Error401 } from "../pages/system/Error401";
import { Error404 } from "../pages/system/Error404";
import { Error500 } from "../pages/system/Error500";

import { Desktop } from "../pages/dashboard/Desktop";
import { UserList } from "../pages/dashboard/Users";
import ChatPage from "../pages/dashboard/Chat/ChatPage";
import EmailLogsPage from "../pages/dashboard/EmailLogs/EmailLogsPage";
import { MarketingEmailPage } from "../pages/dashboard/MarketingEmail/MarketingEmailPage";

import { ModuleCrud } from "../pages/dashboard/Cruds/CrudModules";
import { ModulePackageManage } from "../pages/dashboard/Cruds/ModulePackageManage";
import { PackageItemLinkManage } from "../pages/dashboard/Cruds/PackageItemLinkManage.tsx";

import { CompanyUserByCompany } from "../pages/dashboard/Cruds/CompanyUserByCompany.tsx";

import { CompanyPackageItemBalanceCrud } from "../pages/dashboard/Cruds/CompanyPackageItemBalanceCrud";
import { CompanyPackageItemUsageLogCrud } from "../pages/dashboard/Cruds/CompanyPackageItemUsageLogCrud";

import { PermissionCrud } from "../pages/dashboard/Cruds/PermissonCrud";
import { RoleCrud } from "../pages/dashboard/Cruds/RoleCrud";
import { EmailTemplateList } from "../pages/dashboard/Cruds/EmailTemplateList";
import { EmailTemplateEdit } from "../pages/dashboard/Cruds/EmailTemplateEdit";
import { CompanyList } from "../pages/dashboard/Cruds/CompanyList.tsx";
import { ShowcaseCompanyList } from "../pages/dashboard/Cruds/ShowcaseCompanyList.tsx";
import { CompanyDashboard } from "../pages/dashboard/Cruds/CompanyDashboard.tsx";
import { RolePermissionCrud } from "../pages/dashboard/Cruds/RolePermissionCrud.tsx";
import { LocationManagement } from "../pages/dashboard/LocationManagement.tsx";
import { CompanySubscriptionManagement } from "../pages/dashboard/Cruds/SubscriptionManagement.tsx";
import { ReservationRequestTypeCrud } from "../pages/dashboard/Cruds/ReservationRequestTypeManage.tsx";
import { AircraftManagement } from "../pages/dashboard/Cruds/AircraftManagement.tsx";
import { WetLeaseWantedForms } from "../pages/dashboard/UserForms/WetLeaseWantedForms.tsx";
import { WetLeaseAOGForms } from "../pages/dashboard/UserForms/WetLeaseAOGForms.tsx";
import { WetLeaseAvailableForms } from "../pages/dashboard/UserForms/WetLeaseAvailableForms.tsx";
import { AircraftRequestList } from "../pages/dashboard/UserForms/AircraftRequestList.tsx";
import { DynamicPageEdit } from "../pages/dashboard/Cruds/DynamicPageEdit.tsx";
import { DynamicPageList } from "../pages/dashboard/Cruds/DynamicPageList.tsx";
import { BannerCrud } from "../pages/dashboard/Cruds/BannerCrud.tsx";
import { EngineSaleLeaseAvailableForms } from "../pages/dashboard/UserForms/EngineSaleLeaseAvailableForms.tsx";
import { EngineSaleLeaseWantedForms } from "../pages/dashboard/UserForms/EngineSaleLeaseWantedForms.tsx";
import { AircraftCharterAvailableForms } from "../pages/dashboard/UserForms/AircraftCharterAvailableForms.tsx";
import { AircraftCharterWantedForms } from "../pages/dashboard/UserForms/AircraftCharterWantedForms.tsx";
import { AircraftSaleLeaseAvailableForms } from "../pages/dashboard/UserForms/AircraftSaleLeaseAvailableForms.tsx";
import { AircraftSaleLeaseWantedForms } from "../pages/dashboard/UserForms/AircraftSaleLeaseWantedForms.tsx";
import { BusinessJetWantedForms } from "../pages/dashboard/UserForms/BusinessJetWantedForms.tsx";
import { BusinessJetAvailableForms } from "../pages/dashboard/UserForms/BusinessJetAvailableForms.tsx";
import { EngineTypeCrud } from "../pages/dashboard/Cruds/EngineTypeCrud.tsx";
import { DashboardOverview } from "../pages/dashboard/DashboardOverview.tsx";
import { InvoicePage } from "../pages/dashboard/Invoices/InvoicePage.tsx";
import { LocalSupportList } from "../pages/dashboard/UserForms/LocalSupportList.tsx";
import { LegalSupportList } from "../pages/dashboard/UserForms/LegalSupportList.tsx";
import { AdminTenderList } from "../pages/dashboard/Tenders/AdminTenderList";
import { AdminScmList } from "../pages/dashboard/Scm/AdminScmList";
import { PendingListingsPage } from "../pages/dashboard/PendingListings/PendingListingsPage";
import { ExpiredListingsPage } from "../pages/dashboard/ExpiredListings/ExpiredListingsPage";

export const AppRouter = () => {
  return (
    <RouterRoutes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
      </Route>

      <Route path="/auth/login" element={<Login />} />

      <Route path="/forbidden" element={<Forbidden />} />
      <Route path="/401" element={<Error401 />} />
      <Route path="/500" element={<Error500 />} />
      <Route path="/404" element={<Error404 />} />

      <Route
        element={
          <RequireAuth>
            <MacDashboardLayout />
          </RequireAuth>
        }
      >
        <Route
          path="/dashboard"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN", "SYSTEM_AUDIT"]}>
              <Desktop />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/overview"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN", "SYSTEM_AUDIT"]}>
              <DashboardOverview />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/users"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN", "USER_VIEW"]}>
              <UserList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/chat"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN", "USER_VIEW"]}>
              <ChatPage />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/pending-listings"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <PendingListingsPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/expired-listings"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <ExpiredListingsPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/subscriptions"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanySubscriptionManagement />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/email-logs"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EmailLogsPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/marketing-emails"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <MarketingEmailPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/modules"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <ModuleCrud />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/module/:moduleId/packages"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <ModulePackageManage />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/module/:moduleId/package/:packageId/items"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <PackageItemLinkManage />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/company-list/users/:companyId"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyUserByCompany />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/company-list"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/showcase-companies"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <ShowcaseCompanyList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/company-list/modules/:companyId"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyDashboard />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/company-package-item-balances"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyPackageItemBalanceCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/company-package-item-balance"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyPackageItemBalanceCrud />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/company-package-item-usage-logs"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <CompanyPackageItemUsageLogCrud />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/permissions-table"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <PermissionCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/location-list"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <LocationManagement />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/email-template"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EmailTemplateList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/engine-type"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EngineTypeCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/email-template/edit/:id"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EmailTemplateEdit />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/banners"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <BannerCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/dynamic-page"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <DynamicPageList />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/dynamic-page/edit/:id"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <DynamicPageEdit />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/roles"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <RoleCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/roles-permissions"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <RolePermissionCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/reservation-request-types"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <ReservationRequestTypeCrud />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft/requests"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftRequestList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft/wet-lease/wanted"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <WetLeaseWantedForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft/wet-lease/aog"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <WetLeaseAOGForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft/wet-lease/listing"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <WetLeaseAvailableForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/engine/salelease/listing"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EngineSaleLeaseAvailableForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/engine/salelease/wanted"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <EngineSaleLeaseWantedForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft-charter/listing"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftCharterAvailableForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft-charter/wanted"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftCharterWantedForms />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/aircraft/salelease/listing"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftSaleLeaseAvailableForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/aircraft/salelease/wanted"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftSaleLeaseWantedForms />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/business-jet/wanted"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <BusinessJetWantedForms />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/business-jet/listing"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <BusinessJetAvailableForms />
            </RequireAnyPermission>
          }
        />

        <Route
          path="/dashboard/aircraft-define-types"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AircraftManagement />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/invoices"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <InvoicePage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/addons/local-support"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <LocalSupportList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/addons/legal-support"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <LegalSupportList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/tenders"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AdminTenderList />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/dashboard/scm"
          element={
            <RequireAnyPermission any={["SYSTEM_ADMIN"]}>
              <AdminScmList />
            </RequireAnyPermission>
          }
        />

        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/404" replace />} />
    </RouterRoutes>
  );
};
